import {m as motion, useAnimation} from 'framer-motion'
import React from 'react'
import styled, {useTheme} from 'styled-components'

import {sliceUseCaseActioned, SliceUseCaseActionedAction} from '@/analytics/segment'
import Button from '@/components/button'
import PrismicLink from '@/components/prismic-link'
import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import {Text} from '@/components/text'
import {VisuallyHidden} from '@/components/visually-hidden'
import checkIcon from '@/images/icons/check-white.svg'
import {useSignupModal} from '@/providers/signup-modal'
import {Section} from '@/styles/grid'
import {focusStyles} from '@/styles/utils'
import {durationStringToNumber} from '@/utils/duration-string-to-number'
import mq from '@/utils/media-query'

interface Props {
    title: string
    titleAs: 'h1' | 'h2'
    subtitle: string
    cta: string
    secondaryCtaLabel?: string
    secondaryCtaLink?: PrismicLinkProps
    items: {
        label: string
        value: string
        icon: string
    }[]
}

export const UseCase = React.forwardRef<HTMLDivElement, Props>(
    ({title, titleAs = 'h1', subtitle, cta, secondaryCtaLabel, secondaryCtaLink, items}, ref) => {
        const [selection, setSelection] = React.useState<string[]>([])
        const [checkboxesActioned, setCheckboxesActioned] = React.useState(false)
        const {renderSignupModal} = useSignupModal()
        const animationControls = useAnimation()
        const {animationTimes, spacings} = useTheme()

        const handleFirstInteraction = () => {
            if (checkboxesActioned) {
                return
            }

            sliceUseCaseActioned({
                action: SliceUseCaseActionedAction.Started,
            })

            setCheckboxesActioned(true)
        }

        const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
            if (event.target.checked) {
                handleFirstInteraction()
                setSelection([...selection, event.target.value])
            } else {
                setSelection(selection.filter((item) => item !== event.target.value))
            }

            animationControls.start({
                scale: [1, 1.05, 1],
                transition: {
                    ease: 'easeInOut',
                    duration: durationStringToNumber(animationTimes.slow) / 1000,
                },
            })
        }

        const startTracking = () => {
            if (selection.length === 0) {
                sliceUseCaseActioned({
                    action: SliceUseCaseActionedAction.CompletedWithoutSelection,
                })
            } else {
                sliceUseCaseActioned({
                    action: SliceUseCaseActionedAction.Completed,
                    position: selection.map((item) => items.findIndex((i) => i.value === item) + 1),
                    selection: selection.map((item) => items.find((i) => i.value === item)?.label),
                })
            }
        }

        return (
            <Wrapper ref={ref}>
                <Section>
                    <Text as={titleAs} $variant="heading-large">
                        {title}
                    </Text>

                    <Subtitle $variant="paragraph-large">{subtitle}</Subtitle>

                    <StyledFieldset forwardedAs="fieldset" edgeToEdge>
                        <VisuallyHidden as="legend">{subtitle}</VisuallyHidden>
                        {items.map((item, index) => (
                            <Checkbox key={`${index}-title`}>
                                <VisuallyHidden
                                    as="input"
                                    type="checkbox"
                                    value={item.value}
                                    name="useCases"
                                    onChange={handleChange}
                                    aria-labelledby={`${index}-title`}
                                />
                                <Backdrop $dotSize={spacings.s20} />

                                <Icon $dotSize={spacings.s20} src={item.icon} alt="" />

                                <StyledText id={`${index}-title`} as="span">
                                    {item.label}
                                </StyledText>
                            </Checkbox>
                        ))}
                    </StyledFieldset>

                    <CtasWrapper animate={animationControls}>
                        <StyledButton
                            onClick={() => {
                                renderSignupModal({
                                    signupProps: {form: 'Front', params: {selection}},
                                })
                                startTracking()
                            }}
                        >
                            {cta}
                        </StyledButton>

                        {secondaryCtaLabel && (
                            <StyledButton
                                forwardedAs={PrismicLink}
                                $variant="outline"
                                {...secondaryCtaLink}
                                onClick={startTracking}
                            >
                                {secondaryCtaLabel}
                            </StyledButton>
                        )}
                    </CtasWrapper>
                </Section>
            </Wrapper>
        )
    },
)

const MOBILE_BP = 'sm'
const DESKTOP_BP = 'lg'
const MAX_CHECKBOXES = 6

const Wrapper = styled.div`
    ${mq.min(MOBILE_BP)} {
        text-align: center;
    }
`

const CtasWrapper = styled(motion.create('div'))`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: ${({theme}) => theme.spacings.s12};
    padding: ${({theme}) => theme.spacings.s20} 0;

    ${mq.min('sm')} {
        padding: ${({theme}) => theme.spacings.s40} 0;
        gap: ${({theme}) => theme.spacings.s20};
        flex-wrap: nowrap;
    }
`

const Subtitle = styled(Text)`
    margin-top: ${({theme}) => theme.spacings.s12};

    ${mq.min(MOBILE_BP)} {
        margin-top: ${({theme}) => theme.spacings.s20};
    }
`

const StyledFieldset = styled(Section)`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${({theme}) => theme.spacings.s12};

    ${mq.min(MOBILE_BP)} {
        grid-template-columns: repeat(3, 1fr);
    }

    ${mq.min(DESKTOP_BP)} {
        grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
        gap: ${({theme}) => theme.spacings.s20};
    }
`

interface StyledProps {
    $dotSize: string
}

const Backdrop = styled.span<StyledProps>`
    position: absolute;
    z-index: -1;
    inset: 0;
    border: ${({theme}) => theme.borders.extraMuted};
    border-radius: ${({theme}) => theme.radii.arc20};

    &::before {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        border-radius: inherit;
        opacity: 0;
        background-color: ${({theme}) => theme.colors.background.pink};
        transition: opacity ${({theme}) => theme.transitions.fastOut};
    }

    &::after {
        content: '';
        width: ${({$dotSize}) => $dotSize};
        height: ${({$dotSize}) => $dotSize};
        position: absolute;
        top: ${({theme}) => theme.spacings.s16};
        left: ${({theme}) => theme.spacings.s16};
        border: 2px solid ${({theme}) => theme.shade500};
        border-radius: ${({theme}) => theme.radii.arc4};
        background-color: ${({theme}) => theme.colors.background.default};
    }
`

const Checkbox = styled.label`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: ${({theme}) => theme.spacings.s16};
    text-align: center;
    cursor: pointer;
    user-select: none;

    & input:checked ~ ${Backdrop} {
        &::before {
            opacity: 1;
        }

        &::after {
            background-color: ${({theme}) => theme.colors.background.tertiary};
            border-color: ${({theme}) => theme.colors.background.tertiary};
            background-image: url(${checkIcon});
            background-repeat: no-repeat;
            background-position: center;
            background-size: 10px;
        }
    }

    &:hover ${Backdrop} {
        border-color: ${({theme}) => theme.tokens.shade900};
    }

    & input:focus-visible ~ ${Backdrop} {
        ${focusStyles()}
    }

    &:nth-of-type(n + ${MAX_CHECKBOXES + 1}) {
        display: none;
    }
`

const StyledText = styled(Text)`
    font-size: ${({theme}) => theme.tokens.fontLarge};
    margin-top: auto;
`

const Icon = styled.img<StyledProps>`
    display: block;
    margin: 0 auto ${({theme}) => theme.spacings.s8};
    width: 60px;
    height: 60px;

    ${mq.min(DESKTOP_BP)} {
        width: 100px;
        height: 100px;
        margin: ${({$dotSize}) => $dotSize} auto 0;
    }
`

const StyledButton = styled(Button)`
    ${mq.max(MOBILE_BP)} {
        width: 100%;
    }
`
